import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { MessagesState, AddMessagePayload } from '../types';
import type { Message } from '../../types';

const initialState: MessagesState = {
  items: [],
  isProcessing: false,
  lastThinkingMessageId: null,
  isInterrupted: false,
  interruptionMessage: null,
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<AddMessagePayload>) => {
      const message: Message = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: action.payload.type,
        content: action.payload.content,
        timestamp: new Date().toISOString(),
      };
      
      console.log('💬 Adding message:', { type: message.type, content: message.content.substring(0, 50) + '...' });
      state.items.push(message);
      
      // Track thinking messages
      if (message.type === 'ai-thinking') {
        // Remove any existing thinking message first
        if (state.lastThinkingMessageId) {
          state.items = state.items.filter(msg => msg.id !== state.lastThinkingMessageId);
        }
        state.lastThinkingMessageId = message.id;
        console.log('🤔 Added thinking message:', message.id);
      }
    },
    
    removeMessage: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      console.log('🗑️ Removing message:', messageId);
      
      state.items = state.items.filter(msg => msg.id !== messageId);
      
      // Clear thinking message ID if removed
      if (state.lastThinkingMessageId === messageId) {
        state.lastThinkingMessageId = null;
        console.log('🤔 Cleared thinking message ID');
      }
    },
    
    removeThinkingMessage: (state) => {
      if (state.lastThinkingMessageId) {
        console.log('🗑️ Removing thinking message:', state.lastThinkingMessageId);
        state.items = state.items.filter(msg => msg.id !== state.lastThinkingMessageId);
        state.lastThinkingMessageId = null;
        
        // Also reset processing state when removing thinking message
        state.isProcessing = false;
        console.log('⏹️ Reset processing state after removing thinking message');
      }
    },
    
    clearMessages: (state) => {
      console.log('🧹 Clearing all messages');
      state.items = [];
      state.lastThinkingMessageId = null;
      state.isProcessing = false;
    },
    
    setProcessing: (state, action: PayloadAction<boolean>) => {
      const wasProcessing = state.isProcessing;
      state.isProcessing = action.payload;
      
      if (wasProcessing !== action.payload) {
        console.log('⚙️ Processing state changed:', { from: wasProcessing, to: action.payload });
      }
      
      // If we're no longer processing, remove any thinking messages
      if (!action.payload && state.lastThinkingMessageId) {
        console.log('🗑️ Removing thinking message due to processing=false');
        state.items = state.items.filter(msg => msg.id !== state.lastThinkingMessageId);
        state.lastThinkingMessageId = null;
      }
    },
    
    addSystemMessage: (state, action: PayloadAction<string>) => {
      const message: Message = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'system',
        content: action.payload,
        timestamp: new Date().toISOString(),
      };
      
      console.log('📢 Adding system message:', action.payload.substring(0, 100) + '...');
      state.items.push(message);
    },
    
    // Emergency reset for when things get stuck
    forceResetProcessing: (state) => {
      console.log('🚨 Force resetting processing state');
      state.isProcessing = false;
      
      // Remove any thinking messages
      if (state.lastThinkingMessageId) {
        state.items = state.items.filter(msg => msg.id !== state.lastThinkingMessageId);
        state.lastThinkingMessageId = null;
      }
      
      // Also remove any orphaned thinking messages
      state.items = state.items.filter(msg => msg.type !== 'ai-thinking');
    },
    
    // Clean up orphaned thinking messages (in case of bugs)
    cleanupThinkingMessages: (state) => {
      const thinkingMessages = state.items.filter(msg => msg.type === 'ai-thinking');
      
      if (thinkingMessages.length > 1) {
        console.log('🧹 Cleaning up multiple thinking messages:', thinkingMessages.length);
        // Keep only the most recent thinking message
        const mostRecent = thinkingMessages[thinkingMessages.length - 1];
        state.items = state.items.filter(msg => 
          msg.type !== 'ai-thinking' || msg.id === mostRecent.id
        );
        state.lastThinkingMessageId = mostRecent.id;
      } else if (thinkingMessages.length === 1) {
        state.lastThinkingMessageId = thinkingMessages[0].id;
      } else {
        state.lastThinkingMessageId = null;
      }
    },
    
    // Update message content (for editing/updating)
    updateMessage: (state, action: PayloadAction<{ id: string; content: string }>) => {
      const { id, content } = action.payload;
      const message = state.items.find(msg => msg.id === id);
      if (message) {
        console.log('✏️ Updating message:', id);
        message.content = content;
        message.timestamp = new Date().toISOString();
      }
    },

    setInterrupted: (state, action: PayloadAction<boolean>) => {
      state.isInterrupted = action.payload;
      if (!action.payload) {
        state.interruptionMessage = null;
      }
    },
    
    setInterruptionMessage: (state, action: PayloadAction<string>) => {
      state.interruptionMessage = action.payload;
    },
  },
});

export const {
  addMessage,
  removeMessage,
  removeThinkingMessage,
  clearMessages,
  setProcessing,
  addSystemMessage,
  forceResetProcessing,
  cleanupThinkingMessages,
  updateMessage,
  setInterrupted,
  setInterruptionMessage,
} = messagesSlice.actions;

export default messagesSlice.reducer;